import React, { useMemo, useCallback } from 'react';
import { StyleSheet, View, Pressable, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { isDarkTheme } from '@/constants/Colors';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Button from '@/components/Buttons/Button';
import { useModalStore, useAuthStore } from '@/store';
import useTranslation from '@/hooks/useTranslation';
import PropTypes from 'prop-types';

const createStyles = (colors, isDark) => StyleSheet.create({
  container: {
    marginBottom: 8,
    marginTop: 8,
    maxWidth: '100%',
    paddingHorizontal: 20,
  },
  card: {
    backgroundColor: colors.solid_color_secondary,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#f59e0b40', // Amber/warning color border
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 14,
    gap: 12,
  },
  iconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#f59e0b20', // Amber background
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerText: {
    flex: 1,
  },
  title: {
    marginBottom: 2,
  },
  footer: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    borderTopWidth: 1,
    borderTopColor: colors.border_color_secondary,
    backgroundColor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)',
  },
  dismissButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 6,
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}),
  },
  dismissButtonHovered: {
    backgroundColor: colors.solid_color_tertiary,
  },
  footerButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  cardInfoSection: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderTopWidth: 1,
    borderTopColor: colors.border_color_secondary,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  cardIconSmall: {
    width: 32,
    height: 22,
    borderRadius: 4,
    backgroundColor: colors.solid_color_tertiary,
    alignItems: 'center',
    justifyContent: 'center',
  },
});

function CardExpirationWarningMessage({ warningData, onDismiss }) {
  const { colors, theme } = useTheme();
  const isDark = isDarkTheme(theme);
  const styles = useMemo(() => createStyles(colors, isDark), [colors, isDark]);
  const { openModal } = useModalStore(state => state.actions);
  const { t } = useTranslation();
  
  // Get card info from auth store
  const maskedCardData = useAuthStore(state => state.user?.valmar?.maskedCardData);
  
  // Determine the warning message based on days remaining
  const { title, subtitle } = useMemo(() => {
    const daysRemaining = warningData?.daysRemaining;
    const isExpired = warningData?.isExpired;
    
    if (isExpired) {
      return {
        title: t('messages.cardExpiration.expired'),
        subtitle: t('messages.cardExpiration.expiredSubtitle'),
      };
    }
    
    if (typeof daysRemaining === 'number') {
      if (daysRemaining <= 0) {
        return {
          title: t('messages.cardExpiration.expired'),
          subtitle: t('messages.cardExpiration.expiredSubtitle'),
        };
      }
      if (daysRemaining === 1) {
        return {
          title: t('messages.cardExpiration.expiresTomorrow'),
          subtitle: t('messages.cardExpiration.expiresTomorrowSubtitle'),
        };
      }
      if (daysRemaining <= 7) {
        return {
          title: t('messages.cardExpiration.expiresInDays').replace('%DAYS%', daysRemaining),
          subtitle: t('messages.cardExpiration.expiresInDaysSubtitleUrgent'),
        };
      }
      return {
        title: t('messages.cardExpiration.expiresInDays').replace('%DAYS%', daysRemaining),
        subtitle: t('messages.cardExpiration.expiresInDaysSubtitle'),
      };
    }
    
    return {
      title: t('messages.cardExpiration.expiringSoon'),
      subtitle: t('messages.cardExpiration.expiringSoonSubtitle'),
    };
  }, [warningData?.daysRemaining, warningData?.isExpired, t]);

  // Get card display info
  const getCardTypeName = (cardType) => {
    const types = {
      visa: 'Visa',
      mastercard: 'Mastercard',
      amex: 'Amex',
      discover: 'Discover',
    };
    return types[cardType?.toLowerCase()] || cardType || 'Card';
  };
  
  const cardLastFour = maskedCardData?.maskedNumber?.slice(-4) || '****';
  const cardExpiration = maskedCardData?.expirationDate || warningData?.expirationDate || '';

  const handleUpdatePayment = useCallback(() => {
    if (Platform.OS === 'web') {
      openModal('SettingsModalWeb', { tab: 'subscription' });
      return;
    }
    openModal('SettingsModalMobile', { initialScreen: 'subscription' });
  }, [openModal]);

  const handleDismiss = useCallback(() => {
    if (onDismiss) {
      onDismiss();
    }
  }, [onDismiss]);

  // Determine icon color based on urgency
  const iconColor = useMemo(() => {
    const daysRemaining = warningData?.daysRemaining;
    if (warningData?.isExpired || (typeof daysRemaining === 'number' && daysRemaining <= 0)) {
      return '#dc2626'; // Red for expired
    }
    if (typeof daysRemaining === 'number' && daysRemaining <= 7) {
      return '#ea580c'; // Orange for urgent
    }
    return '#f59e0b'; // Amber for warning
  }, [warningData?.daysRemaining, warningData?.isExpired]);

  return (
    <View style={styles.container}>
      <View style={styles.card}>
        {/* Header */}
        <View style={styles.header}>
          <View style={[styles.iconContainer, { backgroundColor: `${iconColor}20` }]}>
            <Icon 
              name="credit-card" 
              size={20}
              color={iconColor}
            />
          </View>
          <View style={styles.headerText}>
            <Text size="n" weight="semibold" color="primary" style={styles.title}>
              {title}
            </Text>
            <Text size="s" color="secondary">
              {subtitle}
            </Text>
          </View>
        </View>

        {/* Card Info */}
        {maskedCardData && (
          <View style={styles.cardInfoSection}>
            <View style={styles.cardIconSmall}>
              <Icon name="credit-card" size={12} color={colors.text_color_primary} />
            </View>
            <Text size="s" color="secondary">
              {getCardTypeName(maskedCardData?.cardType)} .... {cardLastFour}
              {cardExpiration ? ` • Exp: ${cardExpiration}` : ''}
            </Text>
          </View>
        )}

        {/* Footer */}
        <View style={styles.footer}>
          <Pressable
            onPress={handleDismiss}
            accessibilityRole="button"
            accessibilityLabel="Dismiss warning"
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            style={({ hovered, pressed }) => [
              styles.dismissButton,
              hovered && styles.dismissButtonHovered,
              pressed && { opacity: 0.7 }
            ]}
          >
            <Text size="s" color="tertiary">
              {t('messages.cardExpiration.remindMeLater')}
            </Text>
          </Pressable>
          <View style={styles.footerButtons}>
            <Button
              variant="primary"
              text={t('messages.cardExpiration.updatePayment')}
              size="small"
              prependIcon="credit-card"
              onPress={handleUpdatePayment}
            />
          </View>
        </View>
      </View>
    </View>
  );
}

CardExpirationWarningMessage.propTypes = {
  warningData: PropTypes.shape({
    daysRemaining: PropTypes.number,
    isExpired: PropTypes.bool,
    expirationDate: PropTypes.string,
  }),
  onDismiss: PropTypes.func,
};

CardExpirationWarningMessage.displayName = 'CardExpirationWarningMessage';

export default React.memo(CardExpirationWarningMessage);
